import {Interval} from "luxon";
import {container, inject, singleton} from "tsyringe";
import {ProxyService} from "../../advanced-form/proxy.service";
import {ChangeSet} from "../../domain/entities/change-set.interface";
import {Entity} from "../../domain/entities/entity";
import {Issue} from "../../domain/entities/issue.entity";
import {IssueRepository} from "../../domain/repositories/issue.repository";
import {ArrayHelper} from "../../helpers/array.helper";
import {ObjectHelper} from "../../helpers/object.helper";
import {EntityManager} from "../../persistence/entity.manager";
import {IntervalLoadingGuard} from "../../services/interval-loading.guard";
import {AbstractBaseListener} from "../abstract-base.listener";
import type {BaseMessage} from "../base-message.interface";
import {OnMessage} from "../on-message.decorator";
import {EntityChangedPayload} from "../payloads/entity-changed.payload";
import {EntityPersistedPayload} from "../payloads/entity-persisted.payload";
import {IssueIntervalChangedPayload} from "../payloads/issue-interval-changed.payload";
import {IssueTitleChangedPayload} from "../payloads/issue-title-changed.payload";
import {EntityDeletedPayload} from "../payloads/entity-deleted.payload";
import {EmployeeToIssueAssignment} from "../../domain/entities/employee-to-issue-assignment.entity";
import {Project} from "../../domain/entities/project.entity";
import {TimelineInvalidationService} from "../../services/timeline-invalidation.service";


const filterOnEmployeeToIssueAssignmentFn = (message: BaseMessage<EntityDeletedPayload<EmployeeToIssueAssignment>>) => {
    return message.payload.entity.constructor === EmployeeToIssueAssignment;
}
const filterOnIssueEntityFn = (message: BaseMessage<EntityChangedPayload<Entity>>) => {
    return message.payload.prototype === Issue;
}
const filterOnProjectEntityFn = (message: BaseMessage<EntityChangedPayload<Entity>>) => {
    return message.payload.prototype === Project;
}
const filterOnChangeSetContainsInterval = (message: BaseMessage<EntityChangedPayload<Entity>>) => {
    return Object.keys(message.payload.changeSet).includes(`timeRange`);
}

const filterOnChangeSetContainsTitle = (message: BaseMessage<EntityChangedPayload<Entity>>) => {
    return Object.keys(message.payload.changeSet).includes(`title`);
}

@singleton()
export class EntityChangeListener extends AbstractBaseListener {

    constructor(
        @inject(IssueRepository) private readonly _issueRepository: IssueRepository,
        @inject(EntityManager) private readonly _entityManager: EntityManager
    ) {
        super();
    }

    @OnMessage(`entity.deleted`, filterOnEmployeeToIssueAssignmentFn)
    public async onEmployeeToIssueAssignmentDeleted(messages: Array<BaseMessage<EntityDeletedPayload<EmployeeToIssueAssignment>>>) {
        messages = ArrayHelper.unique(messages, m => m.payload.entity.uid);

        const proxyService = container.resolve(ProxyService);
        for (const message of messages) {
            const issue = await message.payload.entity.issue!;
            const form = await proxyService.get(issue.uid)!;
            if (form) {
                form.commit();
            }
        }
    }

    @OnMessage(`entity.changed`, filterOnIssueEntityFn)
    public async onIssueChanged(messages: BaseMessage<EntityChangedPayload<Issue>>[]): Promise<void> {
        messages = ArrayHelper.unique(messages, m => m.payload.uid);

        const proxyService = container.resolve(ProxyService);
        for (const message of messages) {

            const issue = await this._issueRepository.findOneById(message.payload.uid);
            if (!issue) {
                throw new Error(`No issue entity with uid ${message.payload.uid} found.`);
            }

            // Mark entity as persisted to trigger form state update in dialogs
            this._entityManager.markAsPersisted(issue);

            // Publish entity.persisted event for UI components
            // Entity is now guaranteed to have correct persistence state (isNew() = false)
            this._messageBroker.publish<EntityPersistedPayload<Issue>>({
                type: 'entity.persisted',
                identityHash: issue.uid,
                payload: { uid: issue.uid, entity: issue, prototype: Issue }
            });
            
            issue.commit();

            // Invalidate timeline caches so the re-render picks up the changed issue position
            try {
                const service = container.resolve(TimelineInvalidationService);
                service.invalidateIssue(message.payload.uid);

                // Invalidate new position regions so re-fetch picks up moved issues (Bug 2)
                const startIso = issue.interval.start?.toISODate();
                const endIso = issue.interval.end?.minus({ days: 1 }).toISODate();
                if (startIso && endIso) {
                    const yValues: string[] = [
                        issue.project?.uid,
                        ...issue.employees.map(e => e.uid),
                        issue.status?.uid,
                        issue.type?.uid,
                        issue.creator?.uid,
                        ...issue.keywords.map(k => k.uid)
                    ].filter((v): v is string => v !== undefined && v !== null);

                    for (const [, provider] of service.getProviderEntries()) {
                        if (!provider.axisX.hasValue(startIso) || !provider.axisX.hasValue(endIso)) {
                            continue;
                        }
                        const xStart = provider.axisX.getIndex(startIso)!;
                        const xEnd = provider.axisX.getIndex(endIso)!;

                        for (const yValue of yValues) {
                            if (!provider.axisY.hasValue(yValue)) {
                                continue;
                            }
                            const yIndex = provider.axisY.getIndex(yValue)!;
                            provider.invalidateRegion({
                                indexRangeX: [xStart, xEnd],
                                indexRangeY: [yIndex, yIndex]
                            });
                        }
                    }
                }

                // Trigger re-render AFTER both old + new position invalidation (T4.3)
                service.notifyInvalidated();
            } catch (e) {
                console.warn('[EntityChangeListener] Timeline invalidation failed:', e);
            }

            const form = await proxyService.get(issue.uid)!;
            if (form) {
                form.commit();
            }

            // Notification now handled by GlobalNotificationService

        }
    }

    @OnMessage(`entity.changed`, (message: BaseMessage<EntityChangedPayload<Entity>>) => filterOnIssueEntityFn(message) && filterOnChangeSetContainsInterval(message), 200)
    public async onIssueIntervalChanged(messages: BaseMessage<EntityChangedPayload<Issue>>[]): Promise<void> {
        messages = ArrayHelper.unique(messages, m => m.payload.uid);

        const intervals = [];
        for (const message of messages) {
            const issue = await this._issueRepository.findOneById(message.payload.uid);
            if (!issue) {
                throw new Error(`No issue entity with uid ${message.payload.uid} found.`);
            }

            intervals.push(this._buildInterval(message.payload.changeSet, 'last', issue));
            intervals.push(this._buildInterval(message.payload.changeSet, 'current', issue));
        }

        const intervalLoadingGuard = container.resolve(IntervalLoadingGuard);
        for (const interval of Interval.merge(intervals)) {
            intervalLoadingGuard.removeRange(interval);
            this._messageBroker.publish<IssueIntervalChangedPayload>({
                type: `issue.interval.changed`,
                identityHash: interval.toISO(),
                payload: {interval}
            });
        }
    }

    @OnMessage(`entity.changed`, (message: BaseMessage<EntityChangedPayload<Entity>>) => filterOnIssueEntityFn(message) && filterOnChangeSetContainsTitle(message), 150)
    public async onIssueTitleChanged(messages: BaseMessage<EntityChangedPayload<Issue>>[]): Promise<void> {
        const uids = ArrayHelper.unique(messages.map(m => m.payload.uid));
        const issues = await this._issueRepository.findById(uids);

        for (const issue of issues) {
            this._messageBroker.publish<IssueTitleChangedPayload>({
                type: `issue.title.changed`,
                identityHash: issue.uid,
                payload: {
                    title: issue.toString(),
                    entityUid: issue.uid
                }
            });
        }
    }

    @OnMessage(`entity.changed`, (message: BaseMessage<EntityChangedPayload<Entity>>) => !filterOnIssueEntityFn(message) && !filterOnProjectEntityFn(message), 100)
    public async onNonIssueEntityChanged(messages: BaseMessage<EntityChangedPayload<Entity>>[]): Promise<void> {
        messages = ArrayHelper.unique(messages, m => m.payload.uid);

        for (const message of messages) {
            const entity = await container
                .resolve(EntityManager)
                .getRepositoryForEntity(message.payload.prototype)!
                .findOneById(message.payload.uid);

            if (entity) {
                // Mark entity as persisted
                container.resolve(EntityManager).markAsPersisted(entity);

                // Publish entity.persisted event for UI components
                this._messageBroker.publish<EntityPersistedPayload<Entity>>({
                    type: 'entity.persisted',
                    identityHash: entity.uid,
                    payload: { uid: entity.uid, entity, prototype: message.payload.prototype }
                });

                entity.commit();
                const form = await container.resolve(ProxyService).get(entity.uid)!;
                if (form) {
                    form.commit();
                }
            }
        }
    }

    @OnMessage(`entity.changed`, (message: BaseMessage<EntityChangedPayload<Entity>>) => filterOnProjectEntityFn(message), 100)
    public async onProjectEntityChanged(messages: BaseMessage<EntityChangedPayload<Project>>[]): Promise<void> {
        messages = ArrayHelper.unique(messages, m => m.payload.uid);

        for (const message of messages) {
            const project = await container
                .resolve(EntityManager)
                .getRepositoryForEntity<Project>(message.payload.prototype)!
                .findOneById(message.payload.uid);

            if (project) {
                // Mark entity as persisted
                container.resolve(EntityManager).markAsPersisted(project);

                // Publish entity.persisted event for UI components
                this._messageBroker.publish<EntityPersistedPayload<Project>>({
                    type: 'entity.persisted',
                    identityHash: project.uid,
                    payload: { uid: project.uid, entity: project, prototype: Project }
                });

                project.commit();
                const formId = `${project.uid}-${project.type.key}`;
                const form = await container.resolve(ProxyService).get(formId)!;
                if (form) {
                    form.commit();
                }
            }
        }
    }

    private _buildInterval(changeSet: ChangeSet<Issue>, key: 'last' | 'current', issue: Issue): Interval {
        const empty = {last: undefined, current: undefined};
        return ObjectHelper.getPropertyValue(changeSet.timeRange ?? empty, key) as Interval ?? ObjectHelper.getPropertyValue(issue, `timeRange`);
    }
}